import { readReportedCompletionCount } from './reportedCompletionCount';

// The same adapter and renderer are mirrored in Cortex and Quantum. These
// fields already belong to the page's authorized, dated reporting response.
const count = value => typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
export const PERFORMANCE_METRICS = Object.freeze([{
  key: 'impressions',
  label: 'Impressions',
  unit: 'count',
  color: '#468cff',
  marker: 'square',
  dash: ''
}, {
  key: 'spend',
  label: 'Spend',
  unit: 'currency',
  color: '#ff893c',
  marker: 'diamond',
  dash: ''
}, {
  key: 'clicks',
  label: 'Clicks',
  unit: 'count',
  color: '#00c9df',
  marker: 'circle',
  dash: ''
}, {
  key: 'videoViews',
  label: 'Video views',
  unit: 'count',
  color: '#ff38c7',
  marker: 'diamond',
  dash: '7 3'
}, {
  key: 'engagements',
  label: 'Engagements',
  unit: 'count',
  color: '#efb83c',
  marker: 'triangle',
  dash: '3 3'
}, {
  key: 'videoPlayedTo100Count',
  label: 'Video played to 100% (count)',
  unit: 'count',
  color: '#63d471',
  marker: 'square',
  dash: '9 3 2 3'
}]);
const VIDEO_PLATFORMS = new Set(['youtube', 'meta', 'facebook', 'instagram', 'tiktok']);
const ENGAGEMENT_PLATFORMS = new Set(['youtube', 'meta', 'facebook', 'instagram', 'tiktok', 'linkedin']);
export function formatPerformanceValue(unit, value, compact = false) {
  if (value == null) return 'Unavailable';
  const options = compact ? {
    notation: 'compact',
    maximumFractionDigits: 1
  } : {
    maximumFractionDigits: 20
  };
  if (unit === 'currency') Object.assign(options, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: compact ? 0 : 2
  });
  return new Intl.NumberFormat('en-US', options).format(value);
}
export function buildPerformanceTimeline({
  profile,
  dailyDelivery,
  totals = {},
  selectedMetric,
  siteVisits,
  extraRowsByDate = {}
} = {}) {
  if (!profile || ['ga4', 'shopify'].includes(profile.platformKey)) return null;
  const days = (dailyDelivery?.days || []).filter(day => /^\d{4}-\d{2}-\d{2}$/.test(day.date));
  if (!days.length) return null;
  const platform = profile.platformKey;
  const exactCompletionCount = key => platform === 'youtube' && key === 'videoPlayedTo100Count';
  const read = (metrics, key) => exactCompletionCount(key)
    ? readReportedCompletionCount(metrics?.videoPlayedTo100Count)
    : count(metrics?.[platform === 'youtube' && key === 'videoViews' ? 'trueViewViews' : platform !== 'youtube' && key === 'videoPlayedTo100Count' ? 'videoCompletions' : key]);
  const unavailable = [];
  const candidates = [...PERFORMANCE_METRICS];
  if (selectedMetric && !candidates.some(entry => entry.key === selectedMetric.key)) {
    candidates.unshift({
      ...selectedMetric,
      color: profile.accent,
      marker: 'square',
      dash: ''
    });
  }
  const series = candidates.flatMap(metric => {
    const supported = metric.key === 'videoViews' ? VIDEO_PLATFORMS.has(platform) : metric.key === 'engagements' ? ENGAGEMENT_PLATFORMS.has(platform) : metric.key === 'videoPlayedTo100Count' ? VIDEO_PLATFORMS.has(platform) : true;
    const observations = days.map(day => supported ? read(day.metrics, metric.key) : null);
    const values = observations.filter(value => value != null);
    const total = read(totals, metric.key);
    const sum = values.reduce((a, b) => a + b, 0);
    // Never scale or spread a window total over days. Reject an unexplained
    // overshoot; disclose partial dated coverage without inventing its remainder.
    const tolerance = metric.unit === 'currency' ? 0.005 : 1e-8;
    const nonAdditive = dailyDelivery.nonAdditiveMetricKeys?.includes(metric.key) || dailyDelivery.dateOverlappingMetricKeys?.includes(metric.key);
    const mismatch = !nonAdditive && total != null && sum > total + tolerance;
    const unsafeExactTotal = exactCompletionCount(metric.key) && readReportedCompletionCount(sum) === null;
    if (!values.length || mismatch || unsafeExactTotal) {
      if (PERFORMANCE_METRICS.some(entry => entry.key === metric.key)) unavailable.push({
        ...metric,
        reason: unsafeExactTotal ? 'Dated completion counts cannot be represented exactly' : mismatch ? 'Dated values exceed the matching window total' : 'No authoritative daily observations'
      });
      return [];
    }
    return [{
      ...metric,
      total,
      datedTotal: sum,
      partial: !nonAdditive && total != null && sum < total - tolerance,
      values: Object.fromEntries(days.map((day, i) => [day.date, observations[i]]))
    }];
  });
  if (!series.length) return null;
  const dates = [...new Set(days.map(day => day.date))].sort();
  // Empty calendar slots only break paths. They carry null, never synthesized
  // delivery, and ISO days remain in the reporting timezone supplied upstream.
  const calendar = [];
  for (let ms = Date.parse(dates[0] + 'T00:00:00Z'), end = Date.parse(dates.at(-1) + 'T00:00:00Z'); ms <= end; ms += 86400000) {
    calendar.push(new Date(ms).toISOString().slice(0, 10));
  }
  if (siteVisits?.days?.length) {
    const values = Object.fromEntries(siteVisits.days.filter(day => dates.includes(day.date)).map(day => [day.date, count(day.value)]));
    if (calendar.some(date => values[date] != null)) series.push({
      key: 'siteVisits',
      label: siteVisits.label || 'Site Visits · GA4',
      unit: 'count',
      color: '#ffc24b',
      marker: 'circle',
      dash: '',
      values
    });
  }
  return {
    primaryColor: profile.accent,
    platformLabel: profile.heroLabel || profile.platformKey,
    dates: calendar,
    observedDays: days.length,
    series,
    unavailable,
    extraRowsByDate,
    primaryKey: selectedMetric?.key || 'spend',
    description: 'Reported by day. Counts and USD spend have separate axes. Missing observations are gaps. Toggle metrics to rescale the chart.' + (siteVisits ? ` ${siteVisits.label || 'Site Visits · GA4'} measures the whole site or campaign, not traffic attributed to this platform; read on the count axis.` : '')
  };
}
